import { useEffect, useState } from "react";
import {
    collection,
    getDocs,
    query,
    orderBy,
    doc,
    setDoc,
    serverTimestamp,
} from "firebase/firestore";
import { db } from "./firebase";
import { useNavigate } from "react-router-dom";
import { CLUB_ID, THEME_COLOR } from "./config/club";
import Swal from "sweetalert2";
import { useAuth } from "./context/AuthContext";
import { useTheme } from "./context/ThemeContext";
import AdminAdManager from "./components/admin/AdminAdManager";
import AdminPushNotification from "./components/admin/AdminPushNotification";

export default function AdminDashboard() {
    const { clubSettings: globalClubSettings } = useTheme();
    const [recentSessions, setRecentSessions] = useState([]);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const { user } = useAuth();

    const isSuperAdmin =
        user?.authorization?.super_admin === true;


    const [clubSettings, setClubSettings] = useState(globalClubSettings);

    const [stats, setStats] = useState({
        sessions: 0,
        activeSessions: 0,
        reservations: 0,
        players: 0,
    });

    const navigate = useNavigate();
    const clubId = CLUB_ID;

    const themeAlpha = (opacity) => `${THEME_COLOR}${opacity}`;

    const loadStats = async () => {
        const sessionsSnap = await getDocs(
            query(
                collection(db, "clubs", clubId, "sessions"),
                orderBy("createdAt", "desc")
            )
        );

        const reservationsSnap = await getDocs(
            collection(db, "clubs", clubId, "reservations")
        );

        const sessions = sessionsSnap.docs.map(d => ({
            id: d.id,
            ...d.data()
        }));

        setStats({
            sessions: sessions.length,
            activeSessions: sessions.filter(s => s.status === "active").length,
            reservations: reservationsSnap.size,
            players: 0,
        });

        // 🔥 SET RECENT (limit to 3)
        setRecentSessions(sessions.slice(0, 3));
    };
    useEffect(() => {

        if (!globalClubSettings) return;

        setClubSettings(globalClubSettings);

    }, [globalClubSettings]);
    useEffect(() => {
        loadStats();
    }, []);
    const saveClubSettings = async () => {
        try {

            Swal.fire({
                title: "Saving settings...",
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });

            await setDoc(
                doc(db, "clubs", clubId),
                {
                    ...clubSettings,
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );

            Swal.fire({
                icon: "success",
                title: "Settings Saved",
                text: "Club settings updated successfully.",
                confirmButtonColor: THEME_COLOR,
            });

            setShowSettingsModal(false);

        } catch (error) {

            console.error(error);

            Swal.fire({
                icon: "error",
                title: "Save Failed",
                text: error.message,
            });
        }
    };
    return (
        <div className="container py-5" style={{ maxWidth: "1100px" }}>

            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-5">
                <div>
                    <h1 className="fw-bold mb-1" style={{ color: THEME_COLOR, fontSize: "2.5rem" }}>
                        Admin Dashboard
                    </h1>
                    <p className="text-muted m-0">Overview of your club system</p>
                </div>

                {isSuperAdmin && (
                    <button
                        className="btn px-4 py-2 rounded-pill fw-bold text-white"
                        style={{ backgroundColor: THEME_COLOR }}
                        onClick={() => setShowSettingsModal(true)}
                    >
                        Club Settings
                    </button>
                )}
            </div>

            {/* STATS */}
            <div className="row g-4 mb-5">
                {[
                    { label: "Total Sessions", value: stats.sessions, icon: "bi-collection" },
                    { label: "Active Sessions", value: stats.activeSessions, icon: "bi-broadcast" },
                    { label: "Reservations", value: stats.reservations, icon: "bi-calendar-check" },
                    { label: "Players", value: stats.players, icon: "bi-people-fill" },
                ].map((item, i) => (
                    <div key={i} className="col-6 col-md-3">
                        <div
                            className="p-4 rounded-4 shadow-sm h-100"
                            style={{
                                background: `linear-gradient(135deg, var(--card-bg), ${themeAlpha("08")})`,
                                border: `1px solid ${themeAlpha("10")}`,
                            }}
                        >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="text-muted small">{item.label}</span>
                                <i className={`bi ${item.icon}`} style={{ color: THEME_COLOR }}></i>
                            </div>
                            <h3 className="fw-bold">{item.value}</h3>
                        </div>
                    </div>
                ))}
            </div>

            {/* QUICK ACTIONS */}
            <div className="mb-5">
                <h6 className="text-uppercase small fw-bold text-muted mb-3">Quick Actions</h6>

                <div className="d-flex flex-wrap gap-3">
                    {[
                        { label: "Reservations", path: "/admin/reservations", icon: "bi-calendar-check" },
                        { label: "Sessions", path: "/admin/select-session", icon: "bi-broadcast" },
                        { label: "Players", path: "/admin/players", icon: "bi-people-fill" },
                        { label: "Jersey Orders", path: "/admin/jersey", icon: "bi-bag-fill" },
                        { label: "Tournament", path: "/admin/registrations", icon: "bi-trophy-fill" },
                    ].map((btn) => (
                        <button
                            key={btn.label}
                            className="btn px-4 py-3 rounded-4 fw-bold"
                            style={{
                                background: `${THEME_COLOR}15`,
                                color: THEME_COLOR,
                            }}
                            onClick={() => navigate(btn.path)}
                        >
                            <i className={`${btn.icon} me-2`}></i>
                            {btn.label}
                        </button>
                    ))}
                </div>
            </div>
            {/* AD MANAGER */}
            <div className="mb-5">
                <h6 className="text-uppercase small fw-bold text-muted mb-3">
                    Advertisement Manager
                </h6>

                <AdminAdManager
                    clubId={clubId}
                    THEME_COLOR={THEME_COLOR}
                />

            </div>
            {/* PUSH NOTIFICATION */}
            <div className="mb-5">

                <h6 className="text-uppercase small fw-bold text-muted mb-3">
                    Push Notification
                </h6>

                <AdminPushNotification
                    clubId={clubId}
                    themeColor={THEME_COLOR}
                />
            </div>
            {/* RECENT SESSIONS PREVIEW */}
            <div>
                <h6 className="text-uppercase small fw-bold text-muted mb-3">Recent Sessions</h6>

                <div className="row g-3">
                    {recentSessions.map((s) => (
                        <div key={s.id} className="col-md-4">
                            <div
                                className="card border-0 shadow-sm p-4 rounded-4"
                                style={{
                                    background: `linear-gradient(135deg, var(--card-bg), ${themeAlpha("08")})`,
                                    border: `1px solid ${themeAlpha("10")}`,
                                }}
                            >
                                <span
                                    className="badge mb-2"
                                    style={{
                                        background: `${THEME_COLOR}15`,
                                        color: THEME_COLOR
                                    }}
                                >
                                    {s.status || "Session"}
                                </span>

                                <h5 className="fw-bold">{s.name || "No Name"}</h5>

                                <small className="text-muted">
                                    {s.createdAt?.seconds
                                        ? new Date(s.createdAt.seconds * 1000).toLocaleDateString()
                                        : "No Date"}
                                </small>

                                <button
                                    className="btn btn-sm mt-3 rounded-pill"
                                    style={{ background: THEME_COLOR, color: "white" }}
                                    onClick={() => Swal.fire("Coming Soon!", "di ko pa alam lalagay ko e hahaha", "info")}
                                >
                                    View
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {/* SETTINGS MODAL */}
            {showSettingsModal && (
                <div
                    className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                    style={{
                        background: "rgba(0,0,0,0.45)",
                        zIndex: 9999,
                        backdropFilter: "blur(6px)",
                    }}
                >
                    <div
                        className="rounded-4 shadow-lg p-4 border"
                        style={{
                            width: "95%",
                            maxWidth: "700px",
                            maxHeight: "90vh",
                            overflowY: "auto",
                            background: "var(--bs-body-bg)",
                            color: "var(--bs-body-color)",
                            borderColor: "var(--bs-border-color)",
                        }}
                    >

                        {/* HEADER */}
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <div>
                                <h3
                                    className="fw-bold mb-1"
                                    style={{ color: THEME_COLOR }}
                                >
                                    Club Settings
                                </h3>

                                <small className="text-body-secondary">
                                    Manage your club configuration
                                </small>
                            </div>

                            <button
                                className="btn rounded-circle"
                                style={{
                                    background: "var(--bs-tertiary-bg)",
                                    color: "var(--bs-body-color)",
                                }}
                                onClick={() => setShowSettingsModal(false)}
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>

                        {/* GENERAL SETTINGS */}
                        <div className="mb-4">

                            <h6 className="fw-bold mb-3">
                                General
                            </h6>

                            <div className="mb-3">
                                <label className="form-label fw-semibold">
                                    Club Name
                                </label>

                                <input
                                    type="text"
                                    className="form-control rounded-3"
                                    value={clubSettings.clubName}
                                    onChange={(e) =>
                                        setClubSettings({
                                            ...clubSettings,
                                            clubName: e.target.value,
                                        })
                                    }
                                />
                            </div>

                            <div className="row">

                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-semibold">
                                        Theme Color
                                    </label>

                                    <div
                                        className="p-3 rounded-4 border d-flex align-items-center gap-3"
                                        style={{
                                            background: "var(--bs-tertiary-bg)",
                                            borderColor: "var(--bs-border-color)",
                                        }}
                                    >
                                        <input
                                            type="color"
                                            className="form-control form-control-color"
                                            value={clubSettings.themeColor}
                                            onChange={(e) =>
                                                setClubSettings({
                                                    ...clubSettings,
                                                    themeColor: e.target.value,
                                                })
                                            }
                                        />

                                        <span className="fw-semibold">
                                            {clubSettings.themeColor}
                                        </span>
                                    </div>
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label className="form-label fw-semibold">
                                        Bottom Nav Height
                                    </label>

                                    <input
                                        type="number"
                                        className="form-control rounded-3"
                                        value={clubSettings.bottomNavHeight}
                                        onChange={(e) =>
                                            setClubSettings({
                                                ...clubSettings,
                                                bottomNavHeight: Number(e.target.value),
                                            })
                                        }
                                    />
                                </div>

                            </div>
                        </div>

                        {/* LEVEL SETTINGS */}
                        <div className="mb-4">

                            <div className="d-flex justify-content-between align-items-center mb-3">

                                <h6 className="fw-bold m-0">
                                    Player Levels
                                </h6>

                                <button
                                    className="btn btn-sm rounded-pill text-white"
                                    style={{
                                        backgroundColor: THEME_COLOR
                                    }}
                                    onClick={() => {
                                        setClubSettings({
                                            ...clubSettings,
                                            levels: [
                                                ...clubSettings.levels,
                                                {
                                                    value: clubSettings.levels.length + 1,
                                                    label: "",
                                                    color: "#cccccc",
                                                },
                                            ],
                                        });
                                    }}
                                >
                                    <i className="bi bi-plus-lg me-1"></i>
                                    Add Level
                                </button>
                            </div>

                            <div className="d-flex flex-column gap-3">

                                {clubSettings.levels.map((level, index) => (

                                    <div
                                        key={index}
                                        className="rounded-4 p-3 border"
                                        style={{
                                            background: "var(--bs-tertiary-bg)",
                                            borderColor: "var(--bs-border-color)",
                                        }}
                                    >

                                        <div className="row align-items-center">

                                            <div className="col-md-2 mb-2">

                                                <label className="small text-body-secondary">
                                                    Value
                                                </label>

                                                <input
                                                    type="number"
                                                    className="form-control rounded-3"
                                                    value={level.value}
                                                    onChange={(e) => {

                                                        const updated = [...clubSettings.levels];

                                                        updated[index].value =
                                                            Number(e.target.value);

                                                        setClubSettings({
                                                            ...clubSettings,
                                                            levels: updated,
                                                        });
                                                    }}
                                                />
                                            </div>

                                            <div className="col-md-5 mb-2">

                                                <label className="small text-body-secondary">
                                                    Label
                                                </label>

                                                <input
                                                    type="text"
                                                    className="form-control rounded-3"
                                                    value={level.label}
                                                    onChange={(e) => {

                                                        const updated = [...clubSettings.levels];

                                                        updated[index].label =
                                                            e.target.value;

                                                        setClubSettings({
                                                            ...clubSettings,
                                                            levels: updated,
                                                        });
                                                    }}
                                                />
                                            </div>

                                            <div className="col-md-3 mb-2">

                                                <label className="small text-body-secondary">
                                                    Color
                                                </label>

                                                <div
                                                    className="p-2 rounded-3 border d-flex align-items-center gap-2"
                                                    style={{
                                                        background: "var(--bs-body-bg)",
                                                        borderColor: "var(--bs-border-color)",
                                                    }}
                                                >
                                                    <input
                                                        type="color"
                                                        className="form-control form-control-color"
                                                        value={level.color}
                                                        onChange={(e) => {

                                                            const updated = [...clubSettings.levels];

                                                            updated[index].color =
                                                                e.target.value;

                                                            setClubSettings({
                                                                ...clubSettings,
                                                                levels: updated,
                                                            });
                                                        }}
                                                    />

                                                    <small className="fw-semibold">
                                                        {level.color}
                                                    </small>
                                                </div>
                                            </div>

                                            <div className="col-md-2 text-end">

                                                <button
                                                    className="btn btn-danger rounded-circle"
                                                    onClick={() => {

                                                        const updated =
                                                            clubSettings.levels.filter(
                                                                (_, i) => i !== index
                                                            );

                                                        setClubSettings({
                                                            ...clubSettings,
                                                            levels: updated,
                                                        });
                                                    }}
                                                >
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>

                                        </div>
                                    </div>

                                ))}
                            </div>
                        </div>

                        {/* FOOTER */}
                        <div className="d-flex justify-content-end gap-2">

                            <button
                                className="btn rounded-pill px-4"
                                style={{
                                    background: "var(--bs-tertiary-bg)",
                                    color: "var(--bs-body-color)",
                                }}
                                onClick={() => setShowSettingsModal(false)}
                            >
                                Cancel
                            </button>

                            <button
                                className="btn rounded-pill px-4 text-white fw-bold"
                                style={{
                                    backgroundColor: THEME_COLOR
                                }}
                                onClick={saveClubSettings}
                            >
                                Save Settings
                            </button>

                        </div>

                    </div>
                </div>
            )}
        </div>
    );
}
